import React, { useState, useEffect } from "react";
import styles from "./XIRRCalculator.module.css";

const XIRRCalculator = () => {
  const [investFrequency, setInvestFrequency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [maturityDate, setMaturityDate] = useState("");
  const [maturityAmount, setMaturityAmount] = useState("");
  const [investAmount, setInvestAmount] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
  .toISOString()
  .split("T")[0];
  const formatIndianNumber = (num) => {
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatIN = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }

    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const isValidAmount = (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0;
  };


  const calculateXIRR = () => {
    setError("");
    setResult(null);

    // Input validation
    if (!investFrequency || !startDate || !maturityDate || !maturityAmount || !investAmount) {
      setError("Please enter all fields.");
      return;
    }

    if(!isValidAmount(investAmount)){
      setError("Invalid investment amount");
      return;
    }

    if(!isValidAmount(maturityAmount)){
      setError("Invalid maturity amount");
      return;
    }


    try {

       const cashflow=calculateTotalInvestment({
                            startDate,
                            endDate: maturityDate,
                            amount: investAmount,
                            frequency: investFrequency,
                          });               
       const profolioGrowth=maturityAmount-cashflow.invest 
       const xirr = calculateXIRRPercentage(cashflow.cashFlow, cashflow.cashFlowDates);               
       setResult({totalInvested:cashflow.invest,profolioGrowth:profolioGrowth,xirr:xirr})
      
    } catch (err) {
      setError("An error occurred while calculating XIRR.");
    }
  };

  const calculateTotalInvestment = ({
    startDate,
    endDate,
    amount,
    frequency,
  }) => {    
    if (!startDate || !endDate || !amount || !frequency) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) return 0;

    // convert frequency to months
    const frequencyMap = {
      monthly: 1,
      quarterly: 3,
      halfyearly: 6,
      yearly: 12,
    };

    const stepMonths = frequencyMap[frequency] || 0;

    if (stepMonths === 0) return 0;

    let count = 0;
    let current = new Date(start);
    let matureDate = new Date(start);
    let lastDate= new Date(start);
    let cashFlow=[]
    let cashFlowDates=[]
   
    while (current <= end) {
      count++;
      cashFlow.push(-amount)
      cashFlowDates.push(new Date(current));
      // move to next investment date
      lastDate=new Date(current);
      current.setMonth(current.getMonth() + stepMonths);
      matureDate.setMonth(current.getMonth() + stepMonths);
    }
    if (
        matureDate > end ||
        (matureDate < end && current >= end)
    ) {
        count--;
        cashFlow.pop();
        cashFlow.push(parseFloat(maturityAmount));

        cashFlowDates.pop();
        cashFlowDates.push(new Date(lastDate));
    }


    return {invest: count * Number(amount),cashFlow:cashFlow,cashFlowDates:cashFlowDates};
  };

  const calculateXIRRPercentage = (cashFlows, dates, guess = 0.1) => {  
     
        const firstDate = dates[0];

        const xnpv = (rate) => {
          return cashFlows.reduce((sum, cf, i) => {
            const years =
              (dates[i] - firstDate) / (1000 * 60 * 60 * 24 * 365);

            return sum + cf / Math.pow(1 + rate, years);
          }, 0);
        };

        let low = -0.9999;
        let high = 10;

        for (let i = 0; i < 200; i++) {
          const mid = (low + high) / 2;
          const value = xnpv(mid);

          if (Math.abs(value) < 0.0001) {
            return mid * 100;
          }

          if (value > 0) {
            low = mid;
          } else {
            high = mid;
          }
        }

        return ((low + high) / 2) * 100;
  };

  return (
    <div className={styles.container}>
      
      <div className={styles.card}>

        <h1 className={styles.title}>XIRR Calculator</h1>
        <div className={styles.inputGroup}>
        <div>
      <label>Investment Frequency</label>

      <div className={styles.radioGroup}>
            <label>
              <input
                type="radio"
                name="investFrequency"
                value="monthly"
                checked={investFrequency === "monthly"}
                onChange={(e) => setInvestFrequency(e.target.value)}
              />
              Monthly
            </label>

            <label>
              <input
                type="radio"
                name="investFrequency"
                value="quarterly"
                checked={investFrequency === "quarterly"}
                onChange={(e) => setInvestFrequency(e.target.value)}
              />
              Quarterly
            </label>

            <label>
              <input
                type="radio"
                name="investFrequency"
                value="halfyearly"
                checked={investFrequency === "halfyearly"}
                onChange={(e) => setInvestFrequency(e.target.value)}
              />
              Half Yearly
            </label>

            <label>
              <input
                type="radio"
                name="investFrequency"
                value="yearly"
                checked={investFrequency === "yearly"}
                onChange={(e) => setInvestFrequency(e.target.value)}
              />
              Yearly
            </label>
          </div>
        </div>
      </div>
      <div className={styles.inputGroup}> 
        <div>
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            max={yesterday}
            className={styles.input}
            onChange={(e) =>{setStartDate(e.target.value); setMaturityDate("")}}
            placeholder="e.g. 09-Jun-2021"
            onClick={(e) => e.target.showPicker?.()}
          />
        </div>

        <div>
          <label>Maturity Date</label>
          <input
            type="date"
            value={maturityDate}
            min={startDate}
            className={styles.input}
            onChange={(e) => setMaturityDate(e.target.value)}
            placeholder="e.g. 15-Jun-2026"
            onClick={(e) => e.target.showPicker?.()}
          />
        </div>

        <div>
          <label>Investment Amount (₹)</label>
          <input
            type="number"
            value={investAmount}
            className={styles.input}
            onChange={(e) => setInvestAmount(e.target.value)}
            placeholder="e.g. 10000"
          />
        </div>

        <div>
          <label>Maturity Amount (₹)</label>
          <input
            type="number"
            value={maturityAmount}
            className={styles.input}
            onChange={(e) => setMaturityAmount(e.target.value)}
            placeholder="e.g. 390000"
          />
        </div>
      </div>

      <div className={styles.buttonWrapper}>
        <button type="submit" onClick={calculateXIRR}>Calculate</button>
      </div>

        {error && <p className={styles.error}>{error}</p>}

        {result && (
            <div className={styles.resultCard}>
              <div className={styles.resultRow}>
                <span>Total Investment Amount:</span>
                <strong>₹ {formatIN(result.totalInvested)}</strong>
              </div>

              <div className={styles.resultRow}>
                <span>Portfolio Growth:</span>
                <strong>₹ {formatIN(result.profolioGrowth)}</strong>
              </div>

              <div className={styles.resultRow}>
                <span>XIRR:</span>
                <strong>{result.xirr.toFixed(2)}%</strong>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default XIRRCalculator;
